import { useMutation, useQuery } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { FormParser } from '@src/design-system/forms/parsers';
import { Button, GenericError } from 'design-system';
import {
  AdventureHeaderDocument,
  DefaultAdventureSettingsFragment,
  LinksFragment,
  UpdateAdventureSettingsDocument,
  UpdateBlackoutPeriodsDocument,
} from '@flashpack/graphql';
import { useToast } from '@src/shared/toast/useToast';
import { useRequiredParams } from '@src/shared/useRequiredParams';
import { useSafeMutation } from '@src/shared/useSafeMutation';
import { TextField } from 'mui-rff';
import { Field, Form } from 'react-final-form';
import { useState } from 'react';
import {
  DateRange,
  formatDateRangeString,
  MultiDateRangePicker,
  RangePill,
} from '@src/shared/multi-date-range-picker/MultiDateRangePicker';
import { formatISO, isSameDay, parseISO } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import { Close } from '@mui/icons-material';

type AdventureSettings = Omit<
  LinksFragment & DefaultAdventureSettingsFragment,
  '__typename' | 'blackoutPeriods'
> & {
  blackoutPeriods?: DateRange[] | null;
};

export const AdventureSettingsTab = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { adventureId } = useRequiredParams(['adventureId']);
  const [updateBlackoutPeriods] = useMutation(UpdateBlackoutPeriodsDocument, {});

  const { data, loading, error } = useQuery(AdventureHeaderDocument, {
    variables: {
      id: adventureId,
    },
  });

  const [updateAdventureSettings] = useMutation(UpdateAdventureSettingsDocument, {
    refetchQueries: [AdventureHeaderDocument],
  });
  const { success, error: errorToast } = useToast();
  const { safeMutation } = useSafeMutation();

  const onSubmit = async (fields: AdventureSettings) => {
    const input = {
      id: adventureId,
      ...fields,
      blackoutPeriods:
        fields.blackoutPeriods?.map((period) => ({
          startDate: formatISO(period.start, { representation: 'date' }),
          endDate: formatISO(period.end, { representation: 'date' }),
        })) ?? [],
    };
    await safeMutation(
      updateAdventureSettings({
        variables: {
          input,
        },
      }),
      {
        onSuccess: () => success('Settings updated'),
        onError: () => errorToast('Settings failed to update'),
      },
    );
  };

  if (loading) {
    return <Skeleton variant="text" width="25%" sx={{ fontSize: '30px' }} />;
  }

  if (error) {
    return <GenericError error={error} />;
  }

  if (!data) {
    throw new Error('Data not loaded');
  }

  const { adventure } = data;
  const initialValues: AdventureSettings = {
    faqUrl: adventure.faqUrl,
    bookingsDashboardUrl: adventure.bookingsDashboardUrl,
    feedbackUrl: adventure.feedbackUrl,
    dietaryRequirementsUrl: adventure.dietaryRequirementsUrl,
    defaultCapacity: adventure.defaultCapacity,
    defaultMinToGuarantee: adventure.defaultMinToGuarantee,
    defaultMaxEarlyBirds: adventure.defaultMaxEarlyBirds,
    defaultDaysBeforeDepartureForEB: adventure.defaultDaysBeforeDepartureForEB,
    blackoutPeriods: adventure.blackoutPeriods?.map((period) => ({
      start: parseISO(period.startDate),
      end: parseISO(period.endDate),
    })),
  };
  return (
    <Form
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ handleSubmit, submitting, dirty, form, values }) => {
        return (
          <>
            <form onSubmit={(v) => void handleSubmit(v)}>
              <Grid container spacing={5} sx={{ my: 4 }}>
                <Grid item xs={12} md={6} lg={4}>
                  <Stack gap={4}>
                    <Typography variant="h2">Defaults</Typography>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="bodySingle">Pax. Capacity</Typography>
                      <TextField
                        fullWidth={false}
                        required
                        autoComplete="off"
                        size="small"
                        name="defaultCapacity"
                        InputProps={{ sx: { width: '70px' } }}
                        fieldProps={{
                          parse: FormParser.parseNumber,
                        }}
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="bodySingle">Min. Guarantee</Typography>
                      <TextField
                        fullWidth={false}
                        required
                        autoComplete="off"
                        size="small"
                        name="defaultMinToGuarantee"
                        InputProps={{ sx: { width: '70px' } }}
                        fieldProps={{
                          parse: FormParser.parseNumber,
                        }}
                      />
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="bodySingle">Max. Early Birds</Typography>
                      <TextField
                        fullWidth={false}
                        autoComplete="off"
                        required
                        size="small"
                        name="defaultMaxEarlyBirds"
                        InputProps={{ sx: { width: '70px' } }}
                        fieldProps={{
                          parse: FormParser.parseNumber,
                        }}
                      />
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Stack gap={4}>
                    <Box>
                      <Typography variant="h2">Links</Typography>
                      <Typography
                        sx={{ color: 'principal.grey70' }}
                        variant="captionPara"
                      >
                        To remove a link, leave the field blank and save.
                      </Typography>
                    </Box>
                    <TextField
                      label={`Bookings Dashboard URL`}
                      fullWidth
                      type="url"
                      autoComplete="off"
                      size="small"
                      sx={{ maxWidth: '400px' }}
                      name="bookingsDashboardUrl"
                      placeholder="https://flashpack.clicdata.com/..."
                    />
                    <TextField
                      label={`FAQ Page URL`}
                      fullWidth
                      type="url"
                      autoComplete="off"
                      size="small"
                      sx={{ maxWidth: '400px' }}
                      name="faqUrl"
                      placeholder="https://notion.so/flashpacktravel/..."
                    />
                    <TextField
                      label={`Feedback Dashboard URL`}
                      fullWidth
                      type="url"
                      autoComplete="off"
                      size="small"
                      sx={{ maxWidth: '400px' }}
                      name="feedbackUrl"
                    />
                    <TextField
                      label={`Dietary Requirements Document URL`}
                      fullWidth
                      type="url"
                      autoComplete="off"
                      size="small"
                      sx={{ maxWidth: '400px' }}
                      name="dietaryRequirementsUrl"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <Stack gap={4}>
                    <Box>
                      <Typography variant="h2">Early Birds</Typography>
                      <Typography
                        sx={{ color: 'principal.grey70' }}
                        variant="captionPara"
                      >
                        Discounts policy control center.
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Stack>
                        <Typography variant="bodySingle">Early Birds discount</Typography>
                        <Typography
                          sx={{ color: 'principal.grey70' }}
                          variant="captionPara"
                        >
                          Default days before departure.
                        </Typography>
                      </Stack>
                      <TextField
                        fullWidth={false}
                        required
                        autoComplete="off"
                        size="small"
                        name="defaultDaysBeforeDepartureForEB"
                        InputProps={{ sx: { width: '70px' } }}
                        fieldProps={{
                          parse: FormParser.parseNumber,
                        }}
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="bodySingle">Blackout periods</Typography>
                        <Typography variant="bodyPara">
                          Excluded from early birds discount
                        </Typography>
                        {}
                      </Stack>
                      <Button
                        startIcon={<AddIcon />}
                        variant="outlined"
                        onClick={() => setIsDialogOpen(true)}
                      >
                        Add
                      </Button>
                    </Stack>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {/* Hidden field to register blackoutPeriods with the form */}
                      <Field name="blackoutPeriods" subscription={{}}>
                        {() => null}
                      </Field>
                      {values.blackoutPeriods?.map((period: DateRange) => {
                        return (
                          <RangePill
                            key={`${period.start.getTime()}-${period.end.getTime()}`}
                            title={formatDateRangeString(period.start, period.end)}
                            onRemove={() => {
                              form.change('blackoutPeriods', [
                                ...(
                                  form.getFieldState('blackoutPeriods')?.value ?? []
                                ).filter(
                                  (p: DateRange) =>
                                    !isSameDay(p.start, period.start) ||
                                    !isSameDay(p.end, period.end),
                                ),
                              ]);
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                </Grid>

                <Dialog
                  maxWidth="md"
                  fullWidth
                  open={isDialogOpen}
                  onClose={() => setIsDialogOpen(false)}
                >
                  <Box sx={{ px: 3, py: 2 }}>
                    <Stack direction="row" justifyContent="flex-end">
                      <IconButton
                        aria-label="close"
                        onClick={() => setIsDialogOpen(false)}
                        sx={{
                          color: (theme) => theme.palette.grey[600],
                        }}
                      >
                        <Close sx={{ width: 32, height: 32 }} />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" justifyContent="center">
                      <DialogTitle align="center">Add blackout periods</DialogTitle>
                    </Stack>
                    <DialogContent sx={{ p: 0 }}>
                      <Stack justifyContent="center" alignItems="center">
                        <MultiDateRangePicker
                          dateRanges={values.blackoutPeriods ?? []}
                          onCancel={() => setIsDialogOpen(false)}
                          onSave={async (ranges) => {
                            try {
                              await updateBlackoutPeriods({
                                variables: {
                                  input: {
                                    id: adventureId,
                                    blackoutPeriods: ranges.map((range) => ({
                                      startDate: formatISO(range.start, {
                                        representation: 'date',
                                      }),
                                      endDate: formatISO(range.end, {
                                        representation: 'date',
                                      }),
                                    })),
                                  },
                                },
                              });
                              success('Blackout periods updated');
                            } catch (error) {
                              errorToast('Failed to update blackout periods');
                            } finally {
                              setIsDialogOpen(false);
                            }
                          }}
                        />
                      </Stack>
                    </DialogContent>
                  </Box>
                </Dialog>
              </Grid>
              <LoadingButton
                variant="contained"
                type="submit"
                disabled={!dirty}
                loading={submitting}
                sx={{ width: '120px', my: 2 }}
              >
                Save
              </LoadingButton>
            </form>
          </>
        );
      }}
    />
  );
};
